import { inject, Inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { clamp } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  filter,
  merge,
  Observable,
  repeat,
  startWith,
  SubscriptionLike,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { ShapeState } from '../../../../../../cross-team-shared/annotation/shared/state/shape-state';
import { findClosestSmartSkipFrame } from '../../../../../../cross-team-shared/annotation/shared/utils/keyframe-utils';
import { DebouncedEventTracker } from '../../../../../../cross-team-shared/event-tracking/debounced-event-tracker';
import { outputAnnotationInfoStoreToken } from '../../../../../../outputs/annotation/annotation-shared/annotation-providers';
import { AbstractAnnotationStore } from '../../../../../../outputs/annotation/annotation-shared/state/root/abstract-annotation-store';
import { EventTrackingService } from '../../../../../../shared/event-tracking/event-tracking.service';
import { InFlightExtrapolationRequest } from '../../../../../../team-apis/content/in-flight-extrapolation-request';
import { EventTrackingValue } from '../../../../../../team-apis/core/event-tracking';
import { H3mlStore } from '../../../../../h3ml/state/h3ml-store';
import {
  OutputInfo,
  outputInfoStoreToken,
} from '../../../../../h3ml/state/output-info';
import { TaskWorkspaceStore } from '../../../../../h3ml/state/task-workspace-store';
import { FusionManager } from '../../fusion/fusion-manager';
import { RootShapeSelectionService } from '../../utils/root-shape-selection.service';
import { getFocusedRange } from './focusable-scrubber.utils';

/**
 * @description Used to coordinate the different components that make up the
 * scrubber.
 */
@Injectable()
export class FocusableScrubberService extends InjectableSuperclass {
  readonly framesPerSecond?: number;
  private readonly scrubToFrameEventTracker = new DebouncedEventTracker(
    { id: 'scrub_to_frame', bucket: 'navigation', sub_bucket: 'scrubber' },
    this.eventTrackingService,
  );

  isPlaying$ = new BehaviorSubject(false);
  frameNumber$ = new BehaviorSubject(0);
  maxFrameNumber$: Observable<number>;
  private playSubscription?: SubscriptionLike;
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private sensorRendererStore = inject(outputAnnotationInfoStoreToken)(
    'sensorRendererState',
  );
  private rootShapeSelectionService = inject(RootShapeSelectionService);
  private outputName$ = this.taskWorkspaceStore('selectedOutputName').$;

  constructor(
    private eventTrackingService: EventTrackingService,
    private fusionManager: FusionManager,
    private store: AbstractAnnotationStore,
    private h3mlStore: H3mlStore,
    @Inject(outputInfoStoreToken)
    private outputInfoStore: Store<OutputInfo>,
  ) {
    super();
    this.subscribeTo(
      this.outputName$.pipe(
        switchMap((outputName) =>
          this.fusionManager.getFrameNumberForOutput$(outputName),
        ),
      ),
      (frameNumber) => this.frameNumber$.next(frameNumber),
    );
    this.framesPerSecond = this.getFramesPerSecond();
    this.maxFrameNumber$ = this.outputName$.pipe(
      switchMap((outputName) =>
        this.taskWorkspaceStore.getMaxFrameNumber$(outputName),
      ),
    );
    this.subscribeTo(
      combineLatest([this.frameNumber$, this.maxFrameNumber$]),
      ([frameNumber, maxFrameNumber]) =>
        this.updateFrameRange(frameNumber, maxFrameNumber),
    );
  }

  scrub(frameNumber: number, eventTrackingValue: EventTrackingValue) {
    if (frameNumber === this.getFrameNumber()) {
      return;
    }

    if (eventTrackingValue.method !== 'play') {
      this.pause();
    }

    if (this.isValidFrameNumber(frameNumber)) {
      if (!this.scrubToFrameEventTracker.isDebouncePending()) {
        this.scrubToFrameEventTracker.addBatchContext({
          start_frame: this.getFrameNumber(),
        });
      }
      this.fusionManager.scrubTo(frameNumber);
      this.scrubToFrameEventTracker.trackWithDebounce({
        ...eventTrackingValue,
        is_frame_loaded: this.isFrameLoaded(),
        // the frame number in the context may lag the frame the user has just
        // scrubbed to (because it loads frame before updating the store's frame
        // number)
        frame_number: this.getFrameNumber(),
      });
    }
  }

  play(source: string) {
    if (this.isPlaying$.value) {
      return;
    }

    this.isPlaying$.next(true);
    // +1 because when we start playing it will immediately go to the next frame
    const frameAtPlayStart = this.getFrameNumber() + 1;
    const timeAtPlayStart = performance.now();

    const interval$ = defer(() =>
      timer(this.calculateInterval(frameAtPlayStart, timeAtPlayStart)),
    ).pipe(repeat());

    this.playSubscription = interval$
      .pipe(
        startWith(undefined),
        takeUntil(this.destruction$),
        takeUntil(this.isLastPlayableFrame$()),
      )
      .subscribe({
        next: () => this.scrubFromSource(1, source, 'play'),
        complete: () => {
          this.isPlaying$.next(false);
        },
      });
  }

  pause() {
    if (this.playSubscription) {
      this.playSubscription.unsubscribe();
    }

    this.isPlaying$.next(false);
  }

  scrubFromSource(delta: number, source: string, method: string) {
    this.scrub(
      clamp(this.getFrameNumber() + delta, 0, this.getMaxFrameNumber()),
      { source, method },
    );
  }

  moveToClosestImportantFrame(
    isDirectionForward: boolean,
    eventTrackingValue: EventTrackingValue,
  ) {
    const shapes = this.rootShapeSelectionService.getSelectedShapes();

    const shapesWithInFlightMLE = this.getShapeWithInflightMLE(
      this.store.state().inFlightMlExtrapolationRequests,
      shapes,
    );

    const closestImportantFrame = findClosestSmartSkipFrame(
      isDirectionForward,
      shapesWithInFlightMLE,
      this.getMaxFrameNumber(),
      this.getFrameNumber(),
    );
    this.scrub(closestImportantFrame, {
      ...eventTrackingValue,
      method: isDirectionForward ? 'smart_skip_forward' : 'smart_skip_backward',
    });
  }

  scrubByDelta(delta: number, eventTrackingValue: EventTrackingValue) {
    this.scrub(
      clamp(this.getFrameNumber() + delta, 0, this.getMaxFrameNumber()),
      eventTrackingValue,
    );
  }

  private getMaxFrameNumber(
    outputName = this.taskWorkspaceStore.state().selectedOutputName,
  ) {
    return this.taskWorkspaceStore.getMaxFrameNumber(outputName);
  }

  private getShapeWithInflightMLE(
    inFlightMLRequests: InFlightExtrapolationRequest[],
    shapes: ShapeState[],
  ) {
    return shapes.map((shape) => ({
      ...shape,
      inFlightMLE: inFlightMLRequests.filter(
        ({ shapeId }) => shapeId === shape.index,
      ),
    }));
  }

  private isValidFrameNumber(frameNumber: number) {
    return (
      Number.isInteger(frameNumber) &&
      frameNumber >= 0 &&
      frameNumber <= this.getMaxFrameNumber()
    );
  }

  private isFrameLoaded() {
    return this.sensorRendererStore.state().loadedFrames[this.getFrameNumber()];
  }

  private isLastPlayableFrame$() {
    const lastFrame$ = this.frameNumber$.pipe(
      filter((frameNumber) => frameNumber === this.getMaxFrameNumber()),
    );

    const isCurrentFrameLoading$ = this.frameNumber$.pipe(
      switchMap(
        (frameNumber) =>
          this.sensorRendererStore('loadedFrames')(frameNumber).$,
      ),
      filter((loadedFrame) => !loadedFrame),
    );

    return merge(lastFrame$, isCurrentFrameLoading$);
  }

  private calculateInterval(frameAtPlayStart: number, timeAtPlayStart: number) {
    const elapsedTime = performance.now() - timeAtPlayStart;

    const millisecondsPerFrame = 1000 / this.framesPerSecond!;

    const expectedElapsedTime =
      (this.getFrameNumber() - frameAtPlayStart) * millisecondsPerFrame;

    // if expected is 10ms, but we have 8ms, then playback is too fast
    // next scrub will be 2ms later than usual to compensate
    return Math.max(
      millisecondsPerFrame + (expectedElapsedTime - elapsedTime),
      0,
    );
  }

  private getFramesPerSecond() {
    const fpsInputName = this.h3mlStore
      .state()
      .project.inputs.find(
        (input) =>
          input.sub_type === 'frames_per_second' &&
          input.output_id === this.outputInfoStore.state().output.id,
      )?.name;

    if (fpsInputName) {
      return +this.h3mlStore.state().task.data[fpsInputName];
    }

    return undefined;
  }

  private updateFrameRange(frameNumber: number, maxFrameNumber: number) {
    this.store('focusedAreaRange').set(
      getFocusedRange(frameNumber, maxFrameNumber),
    );
  }

  private getFrameNumber() {
    return this.frameNumber$.value;
  }
}
